import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { trackEvent } from '../../Analytics/TrackedClick';
import { useStatsig } from '../../hooks/useStatsig';
import { performanceStatsAnalyticsEvents, getActivityStatsTraits } from '../../../utils/analytics';
import { PlayerProfileTabIdentifiers } from '../../../utils/constants';
import styles from './performanceStatsDiscoveryBanner.scss';

const PERFORMANCE_STATS_VIEW = 'performance';

const PerformanceStatsDiscoveryBanner = ({ 'data-testid': dataTestId }) => {
    const { checkGate, featureGates } = useStatsig();
    const auth = useSelector((state) => state.auth);
    const playerId = auth?.user?.claimedPlayer?.playerId;
    const isVisible = checkGate(featureGates.PERFORMANCE_STATS_DISCOVERY_BANNER) && !!playerId;
    const hasTrackedImpression = useRef(false);

    useEffect(() => {
        if (isVisible && !hasTrackedImpression.current) {
            hasTrackedImpression.current = true;
            trackEvent(
                performanceStatsAnalyticsEvents.DISCOVERY_BANNER_VIEWED,
                getActivityStatsTraits(auth, playerId, true),
            );
        }
    // auth changes identity on updates unrelated to the banner, so this effect re-runs
    // several times per visit. The ref holds the impression to one per mount.
    }, [auth, isVisible, playerId]);

    if (!isVisible) {
        return null;
    }

    const handleCtaClick = () => {
        trackEvent(
            performanceStatsAnalyticsEvents.DISCOVERY_BANNER_TAPPED,
            getActivityStatsTraits(auth, playerId, true),
        );
    };

    return (
        <div className={styles.banner} data-testid={dataTestId && `${dataTestId}.container`}>
            <span className={styles.text} data-testid={dataTestId && `${dataTestId}.text`}>
                <span className={styles.headline} data-testid={dataTestId && `${dataTestId}.headline`}>
                    New Performance Stats!
                </span>
                <span className={styles.subhead} data-testid={dataTestId && `${dataTestId}.subhead`}>
                    See how you performed in each match compared to your UTR Rating.
                </span>
            </span>
            <Link
                className={styles.cta}
                to={`/profiles/${playerId}?t=${PlayerProfileTabIdentifiers.STATS}&view=${PERFORMANCE_STATS_VIEW}`}
                onClick={handleCtaClick}
                data-testid={dataTestId && `${dataTestId}.cta`}
            >
                View My Stats
            </Link>
        </div>
    );
};

PerformanceStatsDiscoveryBanner.propTypes = {
    'data-testid': PropTypes.string,
};

PerformanceStatsDiscoveryBanner.defaultProps = {
    'data-testid': null,
};

export default PerformanceStatsDiscoveryBanner;
